import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Plus,
  Download,
  Settings,
  Lock,
  Unlock,
  Trash2,
  Clock,
  CheckCircle,
  AlertCircle,
  Loader2,
  Filter,
  Search,
  RefreshCw,
  BarChart3,
  Copy,
  Image
} from 'lucide-react';
import { API } from '../../App';
import AdminLayout from './AdminLayout';
import { 
  downloadSingleQRCode, 
  downloadBrandedCard,
  downloadBrandedCardsAsZip,
  generateBrandedCardsPDF
} from '../../lib/qr-print-utils';

const QRPoolDashboard = () => {
  const navigate = useNavigate();
  const getAdminToken = () => {
    const token = localStorage.getItem('adminToken') || localStorage.getItem('admin_token') || localStorage.getItem('token');
    if (!token) {
      toast.error('Admin login required');
      navigate('/admin/login');
      return null;
    }
    return token;
  };
  const [batches, setBatches] = useState([]);
  const [qrList, setQrList] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    unclaimed: 0,
    active: 0,
    expired: 0,
    disabled: 0
  });
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [quantity, setQuantity] = useState(100);
  const [statusFilter, setStatusFilter] = useState('unclaimed');
  const [searchTerm, setSearchTerm] = useState('');
  const [showPrintModal, setShowPrintModal] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [printLayout, setPrintLayout] = useState(null);
  const [selectedQRs, setSelectedQRs] = useState(new Set());
  const [deleteModal, setDeleteModal] = useState(null); // { qrId, qr, bulk: false } | { ids, activeCount, bulk: true }
  const [page, setPage] = useState(1);
  const [filteredTotal, setFilteredTotal] = useState(0);
  const PAGE_SIZE = 50;

  const markQRsDownloaded = async (qrIds) => {
    try {
      const token = getAdminToken();
      if (!token) return;
      await axios.post(
        `${API}/admin/qr-pool/mark-downloaded`,
        qrIds,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      // Update local list to reflect download timestamp
      const now = new Date().toISOString();
      setQrList(prev => prev.map(qr =>
        qrIds.includes(qr.qr_id) ? { ...qr, last_downloaded_at: now } : qr
      ));
    } catch (err) {
      console.error('Failed to mark QRs as downloaded:', err);
    }
  };

  // Load QR data
  const loadQRData = async (currentPage = page) => {
    try {
      setLoading(true);
      const token = getAdminToken();
      if (!token) {
        return;
      }
      const skip = (currentPage - 1) * PAGE_SIZE;
      const response = await axios.get(
        `${API}/admin/qr-pool/list?status=${statusFilter}&limit=${PAGE_SIZE}&skip=${skip}`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      
      setStats({
        total: response.data.total,
        unclaimed: response.data.unclaimed,
        active: response.data.active,
        expired: response.data.expired,
        disabled: response.data.disabled,
        deleted_by_admin: response.data.deleted_by_admin || 0,
        deleted_by_owner: response.data.deleted_by_owner || 0
      });
      setFilteredTotal(response.data.total);
      setQrList(response.data.qrs || []);
    } catch (error) {
      toast.error('Failed to load QR data');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setSelectedQRs(new Set());
    setPage(1);
    loadQRData(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  useEffect(() => {
    if (page === 1) return; // already handled by statusFilter effect on reset
    setSelectedQRs(new Set());
    loadQRData(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  // Generate new batch
  const handleGenerateBatch = async () => {
    if (quantity < 1 || quantity > 10000) {
      toast.error('Quantity must be between 1 and 10000');
      return;
    }

    setGenerating(true);
    try {
      const token = getAdminToken();
      if (!token) {
        return;
      }
      const response = await axios.post(
        `${API}/admin/qr-pool/generate`,
        { quantity },
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      toast.success(`Generated ${response.data.quantity} QR codes`);
      
      // Add to batches
      if (response.data.batch_id) {
        setBatches(prev => [{
          batch_id: response.data.batch_id,
          quantity: response.data.quantity,
          created_at: new Date().toISOString()
        }, ...prev]);
      }

      setQuantity(100);
      loadQRData();
    } catch (error) {
      const errorMsg = error.response?.data?.message || 'Failed to generate batch';
      toast.error(errorMsg);
    } finally {
      setGenerating(false);
    }
  };

  // Disable QR
  const handleDisableQR = async (qrId) => {
    try {
      const token = getAdminToken();
      if (!token) {
        return;
      }
      await axios.post(
        `${API}/admin/qr-pool/${qrId}/disable`,
        { reason: 'Disabled by admin' },
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      toast.success('QR disabled');
      loadQRData();
    } catch (error) {
      toast.error('Failed to disable QR');
    }
  };

  // Enable QR
  const handleEnableQR = async (qrId) => {
    try {
      const token = getAdminToken();
      if (!token) {
        return;
      }
      await axios.post(
        `${API}/admin/qr-pool/${qrId}/enable`,
        {},
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      toast.success('QR enabled');
      loadQRData();
    } catch (error) {
      toast.error('Failed to enable QR');
    }
  };

  // Extend QR
  const handleExtendQR = async (qrId) => {
    try {
      const token = getAdminToken();
      if (!token) {
        return;
      }
      await axios.post(
        `${API}/admin/qr-pool/${qrId}/extend?days=365`,
        {},
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      toast.success('QR expiry extended');
      loadQRData();
    } catch (error) {
      toast.error('Failed to extend QR');
    }
  };

  // Delete single QR — open modal for linked QRs, direct delete for unclaimed
  const handleDeleteQR = (qrId) => {
    const qr = qrList.find(q => q.qr_id === qrId);
    const isLinked = qr && ['active', 'expired', 'claimed'].includes(qr.status);
    if (isLinked) {
      setDeleteModal({ qrId, qr, bulk: false });
    } else {
      if (!window.confirm(`Delete QR ${qrId}? This cannot be undone.`)) return;
      executeDeleteQR(qrId, false);
    }
  };

  const executeDeleteQR = async (qrId, hardDelete) => {
    try {
      const token = getAdminToken();
      if (!token) return;
      await axios.delete(
        `${API}/admin/qr-pool/${qrId}?force=true${hardDelete ? '&hard_delete=true' : ''}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      toast.success(hardDelete ? 'QR permanently deleted' : 'QR soft-deleted (disabled)');
      setSelectedQRs(prev => { const s = new Set(prev); s.delete(qrId); return s; });
      setDeleteModal(null);
      loadQRData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete QR');
    }
  };

  // Bulk delete selected QRs
  const handleBulkDelete = () => {
    if (selectedQRs.size === 0) return;
    const selectedList = qrList.filter(qr => selectedQRs.has(qr.qr_id));
    const activeCount = selectedList.filter(qr => ['active', 'expired', 'claimed'].includes(qr.status)).length;
    if (activeCount > 0) {
      setDeleteModal({ ids: [...selectedQRs], activeCount, bulk: true });
    } else {
      if (!window.confirm(`Delete ${selectedQRs.size} selected QR(s)?`)) return;
      executeBulkDelete(false);
    }
  };

  const executeBulkDelete = async (hardDelete) => {
    const ids = deleteModal?.bulk ? deleteModal.ids : [...selectedQRs];
    try {
      const token = getAdminToken();
      if (!token) return;
      const response = await axios.post(
        `${API}/admin/qr-pool/bulk-delete?force=true${hardDelete ? '&hard_delete=true' : ''}`,
        ids,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      toast.success(`Deleted ${response.data.deleted_count} QR(s)${response.data.skipped_count > 0 ? `, skipped ${response.data.skipped_count}` : ''}`);
      setSelectedQRs(new Set());
      setDeleteModal(null);
      loadQRData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to bulk delete');
    }
  };

  // Toggle row selection
  const toggleSelectQR = (qrId) => {
    setSelectedQRs(prev => {
      const s = new Set(prev);
      s.has(qrId) ? s.delete(qrId) : s.add(qrId);
      return s;
    });
  };

  // Select / deselect all visible QRs
  const toggleSelectAll = () => {
    const visible = qrList.filter(qr => searchTerm === '' || qr.qr_id.includes(searchTerm.toUpperCase()));
    const allSelected = visible.every(qr => selectedQRs.has(qr.qr_id));
    if (allSelected) {
      setSelectedQRs(prev => { const s = new Set(prev); visible.forEach(qr => s.delete(qr.qr_id)); return s; });
    } else {
      setSelectedQRs(prev => { const s = new Set(prev); visible.forEach(qr => s.add(qr.qr_id)); return s; });
    }
  };

  // Copy QR ID
  const copyQRId = (qrId) => {
    navigator.clipboard.writeText(qrId);
    toast.success('QR ID copied to clipboard');
  };

  const getFrontendActivationUrl = (qrId) => {
    const hostname = window.location.hostname;
    let stableUrl = window.location.origin;

    if (process.env.REACT_APP_FRONTEND_URL) {
      stableUrl = process.env.REACT_APP_FRONTEND_URL.trim();
    } else if (
      hostname === 'app.sideo.in' ||
      hostname === 'sideo.in' ||
      hostname === 'www.sideo.in' ||
      hostname === 'sideo-pre-qr.vercel.app'
    ) {
      stableUrl = 'https://app.sideo.in';
    }

    return `${stableUrl}/q/${qrId}`;
  };

  const copyActivationLink = (qrId) => {
    navigator.clipboard.writeText(getFrontendActivationUrl(qrId));
    toast.success('Activation link copied');
  };

  // Handle download single QR
  const handleDownloadQR = async (qr) => {
    try {
      setExportLoading(true);
      // Use stable frontend URL, not preview/random URLs
      const hostname = window.location.hostname;
      let stableUrl = window.location.origin;

      if (process.env.REACT_APP_FRONTEND_URL) {
        stableUrl = process.env.REACT_APP_FRONTEND_URL.trim();
      } else 
      if (
        hostname === 'app.sideo.in' ||
        hostname === 'sideo.in' ||
        hostname === 'www.sideo.in' ||
        hostname === 'sideo-pre-qr.vercel.app'
      ) {
        stableUrl = 'https://app.sideo.in';
      }

      const qrUrl = `${stableUrl}/q/${qr.qr_id}`;
      await downloadSingleQRCode(qr.qr_id, qrUrl);
      toast.success('QR code downloaded');
    } catch (error) {
      toast.error('Failed to download QR code');
    } finally {
      setExportLoading(false);
    }
  };

  // Handle download branded card
  const handleDownloadBrandedCard = async (qr) => {
    try {
      setExportLoading(true);
      await downloadBrandedCard(qr.qr_id);
      await markQRsDownloaded([qr.qr_id]);
      toast.success('Branded sticker downloaded');
    } catch (error) {
      console.error(error);
      toast.error('Failed to download branded card');
    } finally {
      setExportLoading(false);
    }
  };

  // Handle bulk download branded cards as a single ZIP
  const handleBulkDownloadBrandedPNGs = async () => {
    const qrsToExport = qrList.filter(qr => selectedQRs.has(qr.qr_id));
    if (qrsToExport.length === 0) {
      toast.error('Select at least one QR code first');
      return;
    }
    try {
      setExportLoading(true);
      const qrIds = qrsToExport.map(qr => qr.qr_id);
      await downloadBrandedCardsAsZip(qrIds, {
        fileName: `sideo-branded-qr-codes-${Date.now()}.zip`,
      });
      await markQRsDownloaded(qrIds);
      toast.success(`Downloaded ${qrsToExport.length} branded QR tag(s) as ZIP`);
    } catch (error) {
      console.error(error);
      toast.error('Failed to download branded QR tags');
    } finally {
      setExportLoading(false);
    }
  };

  // Handle export branded cards as PDF
  const handleExportBrandedCards = async () => {
    try {
      setExportLoading(true);
      const qrsToExport = qrList.filter(qr => selectedQRs.has(qr.qr_id));
      
      if (qrsToExport.length === 0) {
        toast.error('Select at least one QR code first');
        return;
      }

      const qrCodesWithDetails = qrsToExport.map(qr => ({
        id: qr.qr_id,
        nickname: qr.vehicle_number || 'Vehicle'
      }));

      await generateBrandedCardsPDF(qrCodesWithDetails, {
        fileName: `sideo-branded-stickers-${new Date().getTime()}.pdf`
      });
      toast.success(`Exported ${qrsToExport.length} branded sticker(s) to PDF`);
    } catch (error) {
      console.error(error);
      toast.error('Failed to export branded cards');
    } finally {
      setExportLoading(false);
    }
  };

  return (
    <AdminLayout>
      <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">QR Pool Management</h1>
              <p className="text-gray-600 mt-1">Manage physical QR code inventory</p>
            </div>
            <button
              onClick={() => navigate('/admin/dashboard')}
              className="text-gray-600 hover:text-gray-900 font-medium"
            >
              ← Back to Admin
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-8">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm">Total QRs</p>
                <p className="text-3xl font-bold text-gray-900">{stats.total}</p>
              </div>
              <BarChart3 className="w-10 h-10 text-gray-400" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm">Unclaimed</p>
                <p className="text-3xl font-bold text-yellow-600">{stats.unclaimed}</p>
              </div>
              <Clock className="w-10 h-10 text-yellow-400" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm">Active</p>
                <p className="text-3xl font-bold text-green-600">{stats.active}</p>
              </div>
              <CheckCircle className="w-10 h-10 text-green-400" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm">Expired</p>
                <p className="text-3xl font-bold text-orange-600">{stats.expired}</p>
              </div>
              <AlertCircle className="w-10 h-10 text-orange-400" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm">Disabled</p>
                <p className="text-3xl font-bold text-red-600">{stats.disabled}</p>
              </div>
              <Lock className="w-10 h-10 text-red-400" />
            </div>
          </div>
        </div>

        {/* Generate Batch Section */}
        <div className="bg-white rounded-lg shadow p-6 mb-8">
          <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
            <Plus className="w-5 h-5" />
            Generate New Batch
          </h2>
          <div className="flex gap-4 flex-col sm:flex-row">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Number of QR Codes
              </label>
              <input
                type="number"
                min="1"
                max="10000"
                value={quantity}
                onChange={(e) => setQuantity(parseInt(e.target.value) || 0)}
                className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:border-indigo-500 focus:outline-none"
                placeholder="100"
              />
              <p className="text-xs text-gray-500 mt-1">Generate 1-10,000 QRs at once</p>
            </div>
            <div className="flex items-end">
              <button
                onClick={handleGenerateBatch}
                disabled={generating}
                className="w-full sm:w-auto bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-400 text-white font-semibold py-2 px-6 rounded-lg transition flex items-center justify-center gap-2"
              >
                {generating ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Plus className="w-4 h-4" />
                    Generate
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Filter Section */}
        <div className="bg-white rounded-lg shadow p-6 mb-8">
          <div className="flex flex-col sm:flex-row gap-4 items-end">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Filter className="w-4 h-4 inline mr-2" />
                Filter by Status
              </label>
              <select
                value={statusFilter}
                onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
                className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:border-indigo-500 focus:outline-none"
              >
                <option value="">All</option>
                <option value="unclaimed">Unclaimed</option>
                <option value="active">Active</option>
                <option value="expired">Expired</option>
                <option value="disabled">Disabled by Admin (Toggle)</option>
                <option value="deleted_by_admin">Deleted by Admin</option>
                <option value="deleted_by_owner">Deleted by Owner</option>
              </select>
            </div>

            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Search className="w-4 h-4 inline mr-2" />
                Search QR ID
              </label>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search..."
                className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:border-indigo-500 focus:outline-none"
              />
            </div>

            <button
              onClick={loadQRData}
              disabled={loading}
              className="bg-gray-600 hover:bg-gray-700 text-white font-semibold py-2 px-6 rounded-lg transition flex items-center gap-2"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>

            <div className="flex gap-2">
              <button
                onClick={handleBulkDownloadBrandedPNGs}
                disabled={exportLoading || selectedQRs.size === 0}
                className="bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-400 text-white font-semibold py-2 px-4 rounded-lg transition flex items-center gap-2"
                title="Download selected QRs as branded tag PNGs"
              >
                <Image className="w-4 h-4" />
                Branded PNG
              </button>
              <button
                onClick={handleExportBrandedCards}
                disabled={exportLoading || selectedQRs.size === 0}
                className="bg-purple-600 hover:bg-purple-700 disabled:bg-gray-400 text-white font-semibold py-2 px-4 rounded-lg transition flex items-center gap-2"
                title="Export selected QRs as branded sticker PDF"
              >
                <Image className="w-4 h-4" />
                Branded PDF
              </button>
              {selectedQRs.size > 0 && (
                <button
                  onClick={handleBulkDelete}
                  className="bg-red-600 hover:bg-red-700 text-white font-semibold py-2 px-4 rounded-lg transition flex items-center gap-2"
                >
                  <Trash2 className="w-4 h-4" />
                  Delete ({selectedQRs.size})
                </button>
              )}
            </div>
          </div>
        </div>

        {/* QR List */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3">
                    <input
                      type="checkbox"
                      className="rounded"
                      checked={qrList.filter(qr => searchTerm === '' || qr.qr_id.includes(searchTerm.toUpperCase())).length > 0 &&
                        qrList.filter(qr => searchTerm === '' || qr.qr_id.includes(searchTerm.toUpperCase())).every(qr => selectedQRs.has(qr.qr_id))}
                      onChange={toggleSelectAll}
                      title="Select all"
                    />
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">QR ID</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Vehicle</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Expiry</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan="6" className="px-6 py-8 text-center">
                      <Loader2 className="w-6 h-6 animate-spin mx-auto text-gray-400" />
                    </td>
                  </tr>
                ) : qrList.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="px-6 py-8 text-center text-gray-500">
                      No QRs found. Generate a batch to get started.
                    </td>
                  </tr>
                ) : (
                  qrList
                    .filter(qr =>
                      searchTerm === '' || qr.qr_id.includes(searchTerm.toUpperCase())
                    )
                    .map(qr => (
                      <tr key={qr.qr_id} className={`hover:bg-gray-50 ${selectedQRs.has(qr.qr_id) ? 'bg-blue-50' : ''}`}>
                        <td className="px-4 py-4">
                          <input
                            type="checkbox"
                            className="rounded"
                            checked={selectedQRs.has(qr.qr_id)}
                            onChange={() => toggleSelectQR(qr.qr_id)}
                          />
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            <code className="bg-gray-100 px-2 py-1 rounded text-sm font-mono">
                              {qr.qr_id}
                            </code>
                            <button
                              onClick={() => copyQRId(qr.qr_id)}
                              className="text-gray-400 hover:text-gray-600"
                            >
                              <Copy className="w-4 h-4" />
                            </button>
                            {qr.last_downloaded_at && (
                              <span
                                className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700"
                                title={`Downloaded: ${new Date(qr.last_downloaded_at).toLocaleString()}`}
                              >
                                ✓ Printed
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`inline-flex px-3 py-1 rounded-full text-xs font-semibold ${
                            qr.status === 'active' ? 'bg-green-100 text-green-800' :
                            qr.status === 'unclaimed' ? 'bg-yellow-100 text-yellow-800' :
                            qr.status === 'expired' ? 'bg-orange-100 text-orange-800' :
                            qr.status === 'disabled' ? 'bg-gray-100 text-gray-700' :
                            qr.status === 'deleted_by_admin' ? 'bg-red-100 text-red-800' :
                            qr.status === 'deleted_by_owner' ? 'bg-orange-100 text-orange-800' :
                            'bg-red-100 text-red-800'
                          }`}>
                            {qr.status === 'disabled' ? 'Disabled' :
                             qr.status === 'deleted_by_admin' ? 'Deleted by Admin' :
                             qr.status === 'deleted_by_owner' ? 'Deleted by Owner' :
                             qr.status}
                          </span>
                          {(qr.status === 'disabled' || qr.status === 'deleted_by_admin' || qr.status === 'deleted_by_owner') && qr.disable_reason && (
                            <div className="text-xs text-red-600 mt-1">{qr.disable_reason}</div>
                          )}
                          {(qr.status === 'disabled' || qr.status === 'deleted_by_admin' || qr.status === 'deleted_by_owner') && qr.disabled_by && (
                            <div className="text-xs text-gray-400">by {qr.disabled_by}</div>
                          )}
                        </td>
                        <td className="px-6 py-4 text-sm">
                          {qr.vehicle_number || '—'}
                        </td>
                        <td className="px-6 py-4 text-sm">
                          {qr.expiry_date
                            ? new Date(qr.expiry_date).toLocaleDateString()
                            : '—'}
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleDownloadBrandedCard(qr)}
                              disabled={exportLoading}
                              className="text-indigo-600 hover:text-indigo-700 disabled:text-gray-400"
                              title="Download Branded QR Tag"
                            >
                              <Image className="w-4 h-4" />
                            </button>
                            {qr.is_disabled ? (
                              <button
                                onClick={() => handleEnableQR(qr.qr_id)}
                                className="text-green-600 hover:text-green-700"
                                title="Enable"
                              >
                                <Unlock className="w-4 h-4" />
                              </button>
                            ) : (
                              <button
                                onClick={() => handleDisableQR(qr.qr_id)}
                                className="text-red-600 hover:text-red-700"
                                title="Disable"
                              >
                                <Lock className="w-4 h-4" />
                              </button>
                            )}
                            {qr.status !== 'unclaimed' && (
                              <button
                                onClick={() => handleExtendQR(qr.qr_id)}
                                className="text-indigo-600 hover:text-indigo-700"
                                title="Extend Expiry"
                              >
                                <RefreshCw className="w-4 h-4" />
                              </button>
                            )}
                            <button
                              onClick={() => handleDeleteQR(qr.qr_id)}
                              className="text-red-500 hover:text-red-700"
                              title="Delete QR"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pagination */}
        {(() => {
          const totalPages = Math.ceil(filteredTotal / PAGE_SIZE);
          if (totalPages <= 1) return null;
          return (
            <div className="flex items-center justify-between mt-4 px-2">
              <p className="text-sm text-gray-500">
                Showing {((page - 1) * PAGE_SIZE) + 1}–{Math.min(page * PAGE_SIZE, filteredTotal)} of {filteredTotal} QRs
              </p>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1 || loading}
                  className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg disabled:opacity-40 hover:bg-gray-50"
                >
                  Previous
                </button>
                <span className="text-sm text-gray-700">
                  Page {page} of {totalPages}
                </span>
                <button
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages || loading}
                  className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg disabled:opacity-40 hover:bg-gray-50"
                >
                  Next
                </button>
              </div>
            </div>
          );
        })()}

        {/* Footer Info */}
        <div className="mt-8 bg-blue-50 rounded-lg p-6 border border-blue-200">
          <p className="text-sm text-blue-700">
            <strong>💡 Tips:</strong>
            <ul className="list-disc list-inside mt-2 space-y-1">
              <li>Generate QRs in bulk before printing stickers</li>
              <li>Download <strong>Branded Cards</strong> for a professional look with sideo branding</li>
              <li>Use <strong>Labels</strong> export for sticker sheets (2x5 per page)</li>
              <li>Share QR activation URL: {window.location.origin}/q/{'<QR_ID>'}</li>
              <li>Each QR gets a 365-day subscription when user claims it</li>
              <li>Admins can disable, extend, or re-enable QRs anytime</li>
            </ul>
          </p>
        </div>
      </div>
      </div>

      {/* Delete Confirmation Modal */}
      {deleteModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-md shadow-xl">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-lg font-bold text-gray-900">
                {deleteModal.bulk
                  ? `Delete ${deleteModal.ids.length} QR code(s)`
                  : `Delete QR ${deleteModal.qrId}`}
              </h2>
              <p className="text-sm text-gray-500 mt-1">
                {deleteModal.bulk
                  ? `${deleteModal.activeCount} of the selected QRs are active/linked to vehicles.`
                  : `This QR is ${deleteModal.qr?.status} and linked to a vehicle.`}
              </p>
            </div>
            <div className="p-6 space-y-3">
              <button
                onClick={() => deleteModal.bulk ? executeBulkDelete(false) : executeDeleteQR(deleteModal.qrId, false)}
                className="w-full flex flex-col items-start px-4 py-3 border-2 border-amber-300 rounded-xl hover:bg-amber-50 text-left transition"
              >
                <span className="font-semibold text-amber-700">Soft Delete (Recommended)</span>
                <span className="text-xs text-gray-500 mt-0.5">Disables the QR. Owner sees "Deleted by Admin". Vehicle and history are preserved.</span>
              </button>
              <button
                onClick={() => deleteModal.bulk ? executeBulkDelete(true) : executeDeleteQR(deleteModal.qrId, true)}
                className="w-full flex flex-col items-start px-4 py-3 border-2 border-red-400 rounded-xl hover:bg-red-50 text-left transition"
              >
                <span className="font-semibold text-red-700">Hard Delete (Permanent)</span>
                <span className="text-xs text-gray-500 mt-0.5">Permanently erases the QR, vehicle record, and all message/call history. Cannot be undone.</span>
              </button>
            </div>
            <div className="px-6 pb-6">
              <button
                onClick={() => setDeleteModal(null)}
                className="w-full py-2 text-gray-500 hover:text-gray-700 text-sm"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default QRPoolDashboard;
